import { useState, useCallback, useEffect } from 'react';

import { supabase } from '@/lib/supabase';
import { PLAY_BILLING_SERVICE_ID, TWA_PACKAGE_NAME } from '@/lib/constants';
import { SUBSCRIPTION_PLANS, GEM_PACKAGES } from '@/lib/ehPrices';
import { useTracking, PAYMENT_EVENTS } from './useTracking';

// As the Digital Goods API is new, the types are not yet in the default TS library.
declare global {
  interface Window {
    getDigitalGoodsService: (serviceId: string) => Promise<DigitalGoodsService | undefined>;
  }
}

export interface PlayProductDetails {
  itemId: string;
  title: string;
  description: string;
  price: {
    currency: string;
    value: number;
  }
}

interface MobileStoreFunctionResponse {
  success: boolean;
  data?: {
    acknowledgementState: number;
    purchaseState: number;
    consumptionState: number;
    quantity: number;
  };
  error?: string;
}

interface DigitalGoodsService {
  getDetails: (skus: string[]) => Promise<PlayProductDetails[]>;
  listPurchases: () => Promise<{ itemId: string, purchaseToken: string }[]>;
  consume: (purchaseToken: string) => Promise<void>;
}

interface PaymentRequestData {
  sku: string;
}

interface PaymentMethodData {
  supportedMethods: string;
  data: PaymentRequestData;
}

interface PaymentDetails {
  total: {
    label: string;
    amount: {
      currency: string;
      value: string;
    };
  };
}

export const usePlayBilling = () => {
  const [billingService, setBillingService] = useState<DigitalGoodsService | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useTracking();

  const initialize = useCallback(async () => {
    if (window && 'getDigitalGoodsService' in window) {
      try {
        const service = await window.getDigitalGoodsService(PLAY_BILLING_SERVICE_ID);
        if (service) {
          setBillingService(service);
          setIsSupported(true);
          if (import.meta.env.DEV) console.log('Google Play Billing is supported!');
        } else {
          setIsSupported(false);
        }
      } catch (e) {
        if (import.meta.env.DEV) console.error('Google Play Billing is not available.', e);
        setError('Google Play Billing is not available.');
        setIsSupported(false);
      }
    } else {
      setIsSupported(false);
    }
  }, []);

  // Init state on mount
  useEffect(() => {
    void initialize();
  }, [initialize]);

  const getProductDetails = useCallback(async (skus: string[]) => {
    if (!billingService) {
      if (import.meta.env.DEV) console.error('Play Billing service not initialized.');
      return null;
    }
    try {
      const details = await billingService.getDetails(skus);
      return details;
    } catch (e) {
      if (import.meta.env.DEV) console.error('Failed to get product details', e);
      setError('Failed to get product details.');
      return null;
    }
  }, [billingService]);

  const purchase = useCallback(async ({ sku, userId }: { sku: string, userId?: string }) => {
    if (!billingService || !userId) {
      if (import.meta.env.DEV) console.error('Play Billing service not initialized or user not logged in.');
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'googlePlay',
        error: 'Play Billing service not initialized or user not logged in.',
      });
      return { success: false };
    }

    const paymentMethods: PaymentMethodData[] = [{
      supportedMethods: PLAY_BILLING_SERVICE_ID,
      data: { sku },
    }];

    const paymentDetails: PaymentDetails = {
      total: {
        label: 'Total',
        amount: { currency: 'USD', value: '0' }, // Not used by Play Billing
      },
    };

    const gemPackage = GEM_PACKAGES.find(g => g.googlePlaySku === sku);
    const plan = SUBSCRIPTION_PLANS.find(p => p.googlePlaySku === sku);

    try {
      const paymentRequest = new PaymentRequest(paymentMethods, paymentDetails);
      const paymentResponse = await paymentRequest.show();
      const { purchaseToken } = paymentResponse.details;

      trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
        paymentMethod: 'googlePlay',
        plan,
        gemPackage,
        billedAmount: gemPackage?.price ?? plan?.price,
      });

      // 1. Verify and acknowledge the purchase on the backend.
      const { data: verificationData, error: verificationError } = await supabase.functions.invoke('eh-mobile-store', {
        body: {
          store: 'googlePlay',
          userId,
          packageName: TWA_PACKAGE_NAME,
          sku,
          token: purchaseToken,
        },
      });

      if (verificationError || !(verificationData as MobileStoreFunctionResponse)?.success) {
        await paymentResponse.complete('fail');
        setError('Purchase verification failed.');
        trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          paymentMethod: 'googlePlay',
          error: `Purchase verification failed: ${
            verificationError?.message
              || (verificationData as MobileStoreFunctionResponse)?.error
              || 'Unknown error'
          }`,
          plan,
          gemPackage,
        });
        return { success: false };
      }

      // The backend will consume the purchase, so no need to call it from client.
      await paymentResponse.complete('success');
      return { success: true };

    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        if (import.meta.env.DEV) console.log('User cancelled the payment request.');
        trackEvent(PAYMENT_EVENTS.PAYMENT_CANCELED, {
          paymentMethod: 'googlePlay',
          plan,
          gemPackage,
          billedAmount: gemPackage?.price ?? plan?.price,
        });
      } else {
        if (import.meta.env.DEV) console.error('Purchase failed', e);
        setError('Purchase failed.');
        trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          paymentMethod: 'googlePlay',
          error: `Purchase failed: ${e instanceof Error ? e.message : String(e)}`,
          plan,
          gemPackage,
          billedAmount: gemPackage?.price ?? plan?.price,
        });
      }
      return { success: false };
    }
  }, [billingService, trackEvent]);

  const checkExistingPurchases = useCallback(async ({ userId }: { userId?: string }) => {
    if (!billingService || !userId) return;
    try {
      const existingPurchases = await billingService.listPurchases();
      for (const p of existingPurchases) {
        if (import.meta.env.DEV) console.log(`User has existing entitlement for ${p.itemId}. Verifying...`);

        const gemPackage = GEM_PACKAGES.find(g => g.googlePlaySku === p.itemId);
        const plan = SUBSCRIPTION_PLANS.find(sp => sp.googlePlaySku === p.itemId);
        if (!plan && !gemPackage) {
          if (import.meta.env.DEV) console.warn(`Unknown SKU found in existing purchases: ${p.itemId}`);
          continue;
        }

        // Verify and process the purchase on the backend.
        const { data: verificationData, error: verificationError } = await supabase.functions.invoke('eh-mobile-store', {
          body: {
            store: 'googlePlay',
            userId,
            packageName: TWA_PACKAGE_NAME,
            sku: p.itemId,
            token: p.purchaseToken,
          },
        });

        if (verificationError || !(verificationData as MobileStoreFunctionResponse)?.success) {
          trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
            paymentMethod: 'googlePlay',
            error: `Existing purchase verification failed: ${
              verificationError?.message
                || (verificationData as MobileStoreFunctionResponse)?.error
                || 'Unknown error'
            }`,
            plan,
            gemPackage,
            sku: p.itemId,
            isExistingPurchase: true,
          });
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) console.error('Failed to list existing purchases', e);
    }
  }, [billingService, trackEvent]);


  return { initialize, isSupported, getProductDetails, purchase, checkExistingPurchases, error };
}; 